import { EPaymentItemRateUnit } from "../PaymentItemRate";
import { schemes, zSchema } from "../../schema-validation";
import {
  EPaymentItemEarningType,
  isRateBasedEarningType,
  isFixedAmountEarningType,
} from "../PaymentItemEarningType";
import { EPaymentItemCategory } from "../EPaymentItemCategory";
import { getPaymentItemTypeLabel } from "../utils";

// Shared validation logic for payment items
const paymentItemSuperRefine = (
  data: {
    category?: EPaymentItemCategory;
    earning_type?: EPaymentItemEarningType;
    rate?: number;
    gross_amount?: number;
    quantity?: number;
    bill_rate?: number | null;
    bill_amount?: number | null;
    work_info?: {
      serviceTypeID?: string;
      workLocationID?: string;
      customerAccountID?: string;
      department_id?: string;
      work_description?: string;
      service_date?: Date;
    };
  },
  ctx: zSchema.RefinementCtx
) => {
  // If earning_type is provided, validate according to it.
  if (data.earning_type) {
    if (isRateBasedEarningType(data.earning_type)) {
      // Require rate for rate-based payment types
      if (!data.rate || data.rate <= 0) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Rate is required and must be greater than 0 for "${getPaymentItemTypeLabel(
            data.earning_type
          )}" payment type`,
          path: ["rate"],
        });
      }
      // Require quantity for rate-based payment types
      if (!data.quantity || data.quantity <= 0) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Quantity is required and must be greater than 0 for "${getPaymentItemTypeLabel(
            data.earning_type
          )}" payment type`,
          path: ["quantity"],
        });
      }
      // Bill amount is not allowed for rate-based payment types
      if (data.bill_amount) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Bill amount is not allowed for "${getPaymentItemTypeLabel(
            data.earning_type
          )}" payment type`,
          path: ["bill_amount"],
        });
      }
      if (data.gross_amount) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Gross amount is not allowed for "${getPaymentItemTypeLabel(
            data.earning_type
          )}" payment type`,
          path: ["gross_amount"],
        });
      }
    } else if (isFixedAmountEarningType(data.earning_type)) {
      // Require grossAmount for fixed amount payment types
      if (!data.gross_amount || data.gross_amount <= 0) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Gross amount is required and must be greater than 0 for "${getPaymentItemTypeLabel(
            data.earning_type
          )}" payment type`,
          path: ["gross_amount"],
        });
      }
      // Don't accept quantity for fixed amount payment types
      if (data.quantity) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Quantity is not allowed for "${getPaymentItemTypeLabel(
            data.earning_type
          )}" payment type`,
          path: ["quantity"],
        });
      }
      // Don't accept rate for fixed amount payment types
      if (data.rate) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Rate is not allowed for "${getPaymentItemTypeLabel(
            data.earning_type
          )}" payment type`,
          path: ["rate"],
        });
      }
      // Don't accept bill_rate for fixed amount payment types
      if (data.bill_rate) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Bill rate is not allowed for "${getPaymentItemTypeLabel(
            data.earning_type
          )}" payment type`,
          path: ["bill_rate"],
        });
      }
    }
    return;
  }

  // earning_type omitted:
  // - If serviceTypeID is provided: allow omitting rate/gross_amount (service type defaults will be used)
  // - If serviceTypeID is NOT provided: require earning_type and either rate or gross_amount
  const hasValidRate = data.rate && data.rate > 0;
  const hasValidGrossAmount = data.gross_amount && data.gross_amount > 0;

  if (data.work_info?.serviceTypeID) {
    // If serviceTypeID is provided, allow omitting rate/gross_amount (they'll be inherited)
    // No additional validation needed here
    return;
  }

  // If serviceTypeID is NOT provided, earning_type is required
  if (!data.earning_type) {
    ctx.addIssue({
      code: zSchema.ZodIssueCode.custom,
      message: "Earning type is required when Service Type is not provided.",
      path: ["earning_type"],
    });
  }

  // If serviceTypeID is NOT provided, require either rate or gross_amount
  if (!hasValidRate && !hasValidGrossAmount) {
    ctx.addIssue({
      code: zSchema.ZodIssueCode.custom,
      message:
        "Either `rate` or `gross_amount` is required and must be greater than 0 when `work_info.serviceTypeID` is not provided.",
      path: ["rate", "gross_amount"],
    });
  }
};

// Base payment item fields for creation (without auto-generated fields)
const createPaymentItemFields = {
  work_info: zSchema
    .object({
      serviceTypeID: schemes.mongoOrUuid().optional(),
      workLocationID: schemes.mongoOrUuid().optional(),
      customerAccountID: schemes.uuid().optional(),
      departmentID: zSchema.string().optional(),
      work_description: zSchema.preprocess(
        (val) =>
          val === "" || val === null || val === undefined ? undefined : val,
        zSchema
          .string()
          .trim()
          .min(1, "Work description is required")
          .optional()
      ),
      service_date: zSchema.preprocess(
        (val) => (val === null || val === undefined ? undefined : val),
        zSchema.coerce.date().optional()
      ),
    })
    .strict()
    .optional(),
  earning_type: zSchema.nativeEnum(EPaymentItemEarningType).optional(),
  quantity: zSchema.coerce
    .number()
    .positive("Quantity must be greater than 0")
    .optional(),
  rate: zSchema.coerce
    .number()
    .positive("Rate must be greater than 0")
    .optional(),
  rate_unit: zSchema.nativeEnum(EPaymentItemRateUnit).optional(),
  gross_amount: zSchema.coerce
    .number()
    .positive("Gross amount must be greater than 0")
    .optional(),
  bill_rate: zSchema.preprocess(
    (val) =>
      val === "" || val === null || val === undefined || val === 0 ? null : val,
    zSchema
      .union([
        zSchema.null(),
        zSchema.coerce.number().positive("Bill rate must be greater than 0"),
      ])
      .optional()
  ),
  bill_amount: zSchema.preprocess(
    (val) =>
      val === "" || val === null || val === undefined || val === 0 ? null : val,
    zSchema
      .union([
        zSchema.null(),
        zSchema.coerce.number().positive("Bill amount must be greater than 0"),
      ])
      .optional()
  ),
  metadata: zSchema.record(zSchema.unknown()).optional(),
  category: zSchema.nativeEnum(EPaymentItemCategory).optional(),
};

// Payment item schema for forms/input (includes validation, excludes auto-generated fields like paymentItemID)
export const paymentItemFormSchema = zSchema
  .object(createPaymentItemFields)
  .extend({
    paymentItemID: schemes.uuid().optional(),
  })
  .strict()
  .superRefine(paymentItemSuperRefine);

// Base payment item schema for response (includes creation fields + auto-generated fields)
export const basePaymentItemResponseSchema = zSchema
  .object({
    ...createPaymentItemFields,
    createdAt: schemes.isoDate(),
    updatedAt: schemes.isoDate(),
    paymentItemID: schemes.uuid(),
  })
  .strict();

// Full create schema with IDs (includes same validation)
export const createPaymentItemSchema = zSchema
  .object({
    ...createPaymentItemFields,
    companyID: schemes.companyID(),
    contractorID: schemes.mongoOrUuid(),
    contractorPaymentID: schemes.mongoObjectId(),
  })
  .strict()
  .superRefine(paymentItemSuperRefine);

export const updatePaymentItemSchema = zSchema
  .object({
    companyID: schemes.companyID(),
    paymentItemID: schemes.uuid(),
    work_info: zSchema
      .object({
        serviceTypeID: schemes.mongoOrUuid().optional(),
        workLocationID: schemes.mongoOrUuid().optional(),
        customerAccountID: schemes.uuid().optional(),
        departmentID: zSchema.string().optional(),
        work_description: zSchema.preprocess(
          (val) =>
            val === "" || val === null || val === undefined ? undefined : val,
          zSchema.string().trim().min(1).optional()
        ),
        service_date: zSchema.preprocess(
          (val) => (val === null || val === undefined ? undefined : val),
          zSchema.coerce.date().optional()
        ),
      })
      .strict()
      .optional(),
    earning_type: zSchema.nativeEnum(EPaymentItemEarningType).optional(),
    quantity: zSchema.coerce
      .number()
      .positive("Quantity must be greater than 0")
      .optional(),
    rate: zSchema.coerce
      .number()
      .positive("Rate must be greater than 0")
      .optional(),
    rate_unit: zSchema.nativeEnum(EPaymentItemRateUnit).optional(),
    gross_amount: zSchema.coerce
      .number()
      .positive("Gross amount must be greater than 0")
      .optional(),
    bill_rate: zSchema.preprocess(
      (val) =>
        val === "" || val === null || val === undefined || val === 0
          ? null
          : val,
      zSchema
        .union([
          zSchema.null(),
          zSchema.coerce.number().positive("Bill rate must be greater than 0"),
        ])
        .optional()
    ),
    bill_amount: zSchema.preprocess(
      (val) =>
        val === "" || val === null || val === undefined || val === 0
          ? null
          : val,
      zSchema
        .union([
          zSchema.null(),
          zSchema.coerce
            .number()
            .positive("Bill amount must be greater than 0"),
        ])
        .optional()
    ),
    metadata: zSchema.record(zSchema.unknown()).optional(),
    category: zSchema.nativeEnum(EPaymentItemCategory).optional(),
  })
  .strict();
